import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { gramsFromPortions, normalizeSteps, RECIPE_MAX_STEPS } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalFood, LocalRecipe, LocalRecipeIngredient } from '../../db/local.js';
import {
  addFreeIngredient,
  addIngredient,
  assignIngredient,
  listFoods,
  listIngredients,
  moveIngredient,
  removeIngredient,
  setRecipeSteps,
  stepsOf,
  updateIngredientAmount,
} from '../../db/nutrition.js';
import { removeRecipeImage, uploadRecipeImage } from '../../db/recipeImages.js';
import { UnlockEncryption } from '../EncryptionSetting.js';
import type { ImageOutcome } from '../../db/recipeImages.js';
import { FoodForm, toNumber } from '../nutrition/FoodForm.js';
import { RecipeForm } from './RecipeForm.js';
import { RecipeImage } from './RecipeImage.js';

/**
 * Ein Gericht bearbeiten — in Abschnitten, jeder für sich gespeichert: Bild,
 * Grunddaten, Zutaten, Schritte. Wer nur eine Zutat ergänzt, soll nicht das
 * ganze Formular durchgehen müssen.
 */
export function RecipeEditor({
  recipe,
  onDone,
}: {
  recipe: LocalRecipe;
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <h2 className="font-display text-2xl font-bold break-words">
        {t('dish.editing', { name: recipe.name })}
      </h2>

      <ImageSection recipe={recipe} />

      <section aria-labelledby="edit-basics" className="flex flex-col gap-2">
        <h3 id="edit-basics" className="kw-kicker">
          {t('dish.basics')}
        </h3>
        <RecipeForm recipe={recipe} onDone={() => setSaved(true)} />
        <p role="status" className="text-sm text-good">
          {saved ? t('dish.saved') : null}
        </p>
      </section>

      <IngredientsSection recipe={recipe} />
      <StepsSection recipe={recipe} />

      <button type="button" onClick={onDone} className="kw-btn">
        {t('dish.done')}
      </button>
    </div>
  );
}

// --- Bild ----------------------------------------------------------------------

function ImageSection({ recipe }: { recipe: LocalRecipe }): React.JSX.Element {
  const { t } = useTranslation();
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ImageOutcome | null>(null);

  const message =
    outcome === 'offline'
      ? t('dish.imageOffline')
      : outcome === 'tooLarge'
        ? t('dish.imageTooLarge')
        : outcome === 'quotaFull'
          ? t('storage.fullRecipe')
          : outcome === 'locked'
            ? t('dish.imageLocked')
            : outcome === 'failed'
              ? t('dish.imageFailed')
              : null;

  return (
    <section aria-labelledby="edit-image" className="kw-tile flex flex-col gap-3">
      <h3 id="edit-image" className="kw-kicker">
        {t('dish.image')}
      </h3>
      <RecipeImage
        recipe={recipe}
        className={`w-full rounded-xl ${recipe.imageAt ? 'aspect-[16/9]' : 'aspect-[5/1]'}`}
      />
      {/* Ein Dateifeld: iOS bietet dabei Kamera und Mediathek an. */}
      <input
        ref={input}
        id={inputId}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          setBusy(true);
          setOutcome(null);
          void uploadRecipeImage(recipe.id, file).then((result) => {
            setBusy(false);
            setOutcome(result);
          });
        }}
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => input.current?.click()}
          className="kw-btn kw-btn-small flex-1"
        >
          {busy ? t('dish.imageBusy') : recipe.imageAt ? t('dish.imageChange') : t('dish.imageAdd')}
        </button>
        {recipe.imageAt ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void removeRecipeImage(recipe.id).then((result) => {
                setBusy(false);
                setOutcome(result === 'done' ? null : result);
              });
            }}
            className="kw-btn kw-btn-quiet kw-btn-small text-danger"
          >
            {t('dish.imageRemove')}
          </button>
        ) : null}
      </div>
      {message ? (
        <p role="alert" className="text-sm text-danger">
          {message}
        </p>
      ) : null}
      {outcome === 'locked' ? <UnlockEncryption /> : null}
    </section>
  );
}

// --- Zutaten ---------------------------------------------------------------------

type AddMode = 'food' | 'free' | 'newFood';

function IngredientsSection({ recipe }: { recipe: LocalRecipe }): React.JSX.Element {
  const { t } = useTranslation();
  const ingredients = useLiveQuery(
    () => listIngredients(recipe.id, db),
    [recipe.id],
    [] as LocalRecipeIngredient[],
  );
  const foods = useLiveQuery(() => listFoods(db), [], [] as LocalFood[]);
  const byId = new Map(foods.map((food) => [food.id, food]));
  const [mode, setMode] = useState<AddMode>('food');
  const [preselect, setPreselect] = useState<string | null>(null);

  return (
    <section aria-labelledby="edit-ingredients" className="kw-tile flex flex-col gap-3">
      <h3 id="edit-ingredients" className="kw-kicker">
        {t('nutrition.recipe.ingredients')}
      </h3>
      <p className="text-xs text-ink-muted">
        {recipe.macroSource === 'ingredients'
          ? t('nutrition.recipe.ingredientsWin')
          : t('nutrition.recipe.noIngredients')}
      </p>

      {ingredients.length > 0 ? (
        <ul className="flex flex-col divide-y divide-line">
          {ingredients.map((entry, index) => (
            <IngredientRow
              key={entry.id}
              entry={entry}
              food={entry.foodId ? byId.get(entry.foodId) : undefined}
              foods={foods}
              first={index === 0}
              last={index === ingredients.length - 1}
            />
          ))}
        </ul>
      ) : null}

      <div role="group" aria-label={t('dish.addHow')} className="flex flex-wrap gap-1">
        {(['food', 'newFood', 'free'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={mode === option}
            onClick={() => setMode(option)}
            className="kw-toggle"
          >
            {t(`dish.add.${option}`)}
          </button>
        ))}
      </div>

      {mode === 'food' ? (
        <AddFoodIngredient
          recipeId={recipe.id}
          foods={foods}
          preselect={preselect}
          onUsed={() => setPreselect(null)}
        />
      ) : mode === 'newFood' ? (
        <FoodForm
          onDone={(foodId) => {
            // Gleich im Rezept weiter: Das neue Lebensmittel steht schon ausgewählt.
            if (foodId) setPreselect(foodId);
            setMode('food');
          }}
        />
      ) : (
        <AddFreeLine recipeId={recipe.id} />
      )}
    </section>
  );
}

function IngredientRow({
  entry,
  food,
  foods,
  first,
  last,
}: {
  entry: LocalRecipeIngredient;
  food: LocalFood | undefined;
  foods: LocalFood[];
  first: boolean;
  last: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const name =
    entry.foodId === null ? (entry.label ?? '') : (food?.name ?? t('nutrition.recipe.unknownFood'));
  const amount =
    entry.foodId === null
      ? t('dish.noNutrition')
      : entry.portionCount !== null && entry.portionCount !== undefined && food?.portionName
        ? `${entry.portionCount} × ${food.portionName} · ${Math.round(entry.grams ?? 0)} g`
        : `${Math.round(entry.grams ?? 0)} g`;

  return (
    <li className="flex flex-col gap-2 py-2">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 text-sm">
          <span className="block break-words">{name}</span>
          <span className="block text-xs text-ink-muted">{amount}</span>
        </span>
        <button
          type="button"
          disabled={first}
          onClick={() => void moveIngredient(entry.id, -1, db)}
          aria-label={t('dish.moveUp', { name })}
          className="kw-btn kw-btn-quiet kw-btn-small w-11 px-0"
        >
          ↑
        </button>
        <button
          type="button"
          disabled={last}
          onClick={() => void moveIngredient(entry.id, 1, db)}
          aria-label={t('dish.moveDown', { name })}
          className="kw-btn kw-btn-quiet kw-btn-small w-11 px-0"
        >
          ↓
        </button>
        <button
          type="button"
          aria-expanded={editing}
          onClick={() => setEditing((value) => !value)}
          aria-label={
            entry.foodId !== null ? t('dish.changeAmount', { name }) : t('dish.assign', { name })
          }
          className="kw-btn kw-btn-quiet kw-btn-small w-11 px-0"
        >
          ✎
        </button>
        <button
          type="button"
          onClick={() => void removeIngredient(entry.id, db)}
          aria-label={t('dish.removeNamed', { name })}
          className="kw-btn kw-btn-quiet kw-btn-small w-11 px-0 hover:border-danger hover:text-danger"
        >
          ×
        </button>
      </div>
      {editing && entry.foodId === null ? (
        <AssignForm entry={entry} foods={foods} onDone={() => setEditing(false)} />
      ) : null}
      {editing && food ? (
        <AmountForm
          food={food}
          initial={entry}
          submitLabel={t('nutrition.library.save')}
          onSubmit={(amount) => {
            void updateIngredientAmount(entry.id, amount, db).then(() => setEditing(false));
          }}
        />
      ) : null}
    </li>
  );
}

/**
 * Freie Zeile einem Lebensmittel zuordnen („200 g rote Linsen" → Rote Linsen,
 * 200 g). Die Zeile bleibt als Hilfe stehen, bis zugeordnet ist.
 */
function AssignForm({
  entry,
  foods,
  onDone,
}: {
  entry: LocalRecipeIngredient;
  foods: LocalFood[];
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const foodId = useId();
  const [selected, setSelected] = useState('');
  const food = foods.find((candidate) => candidate.id === selected);

  if (foods.length === 0) {
    return <p className="text-xs text-ink-muted">{t('nutrition.recipe.needFoods')}</p>;
  }

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-line p-3">
      <p className="text-xs text-ink-muted">{t('dish.assignHint', { line: entry.label ?? '' })}</p>
      <label htmlFor={foodId} className="flex flex-col gap-1">
        <span className="kw-label">{t('nutrition.recipe.food')}</span>
        <select
          id={foodId}
          value={selected}
          onChange={(event) => setSelected(event.target.value)}
          className="kw-field"
        >
          <option value="">{t('nutrition.recipe.pickFood')}</option>
          {foods.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.brand ? `${candidate.name} (${candidate.brand})` : candidate.name}
            </option>
          ))}
        </select>
      </label>
      {food ? (
        <AmountForm
          key={food.id}
          food={food}
          initial={{ ...entry, grams: gramsIn(entry.label) }}
          submitLabel={t('dish.assignConfirm')}
          onSubmit={(amount) => {
            void assignIngredient(entry.id, { foodId: food.id, ...amount }, db).then(onDone);
          }}
        />
      ) : null}
    </div>
  );
}

/** „200 g rote Linsen" → 200 als Vorschlag. Andere Einheiten raten wir nicht. */
function gramsIn(label: string | null | undefined): number | null {
  const match = /(\d+(?:[.,]\d+)?)\s*g\b/i.exec(label ?? '');
  return match ? Number(match[1]!.replace(',', '.')) : null;
}

function AddFoodIngredient({
  recipeId,
  foods,
  preselect,
  onUsed,
}: {
  recipeId: string;
  foods: LocalFood[];
  preselect: string | null;
  onUsed: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const foodId = useId();
  const [selected, setSelected] = useState(preselect ?? '');

  useEffect(() => {
    if (preselect) setSelected(preselect);
  }, [preselect]);

  if (foods.length === 0) {
    return <p className="text-xs text-ink-muted">{t('nutrition.recipe.needFoods')}</p>;
  }

  const food = foods.find((entry) => entry.id === selected);

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={foodId} className="flex flex-col gap-1">
        <span className="kw-label">{t('nutrition.recipe.food')}</span>
        <select
          id={foodId}
          value={selected}
          onChange={(event) => setSelected(event.target.value)}
          className="kw-field"
        >
          <option value="">{t('nutrition.recipe.pickFood')}</option>
          {foods.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.brand ? `${entry.name} (${entry.brand})` : entry.name}
            </option>
          ))}
        </select>
      </label>
      {food ? (
        <AmountForm
          key={food.id}
          food={food}
          submitLabel={t('nutrition.recipe.addIngredient')}
          onSubmit={(amount) => {
            void addIngredient(recipeId, food.id, amount.grams, db, amount.portionCount).then(
              () => {
                setSelected('');
                onUsed();
              },
            );
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Menge in Gramm — oder in der Portion, die am Lebensmittel hinterlegt ist
 * („2 Scheiben"). Gespeichert wird immer auch das Gramm, daraus gerechnet.
 */
function AmountForm({
  food,
  initial,
  submitLabel,
  onSubmit,
}: {
  food: LocalFood;
  initial?: LocalRecipeIngredient;
  submitLabel: string;
  onSubmit: (amount: { grams: number; portionCount: number | null }) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const amountId = useId();
  const canPortion = food.portionGrams !== null && food.portionGrams > 0 && !!food.portionName;
  const startInPortions =
    canPortion && initial?.portionCount !== null && initial?.portionCount !== undefined;
  const [unit, setUnit] = useState<'g' | 'portion'>(startInPortions ? 'portion' : 'g');
  const [value, setValue] = useState(
    startInPortions ? String(initial?.portionCount) : String(initial?.grams ?? 100),
  );

  const number = toNumber(value);
  const grams =
    number === null
      ? null
      : unit === 'portion'
        ? gramsFromPortions(number, food.portionGrams)
        : number;

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (grams === null || grams <= 0) return;
        onSubmit({ grams, portionCount: unit === 'portion' ? number : null });
      }}
    >
      <div className="flex items-end gap-2">
        <label htmlFor={amountId} className="flex w-28 flex-col gap-1">
          <span className="kw-label">{t('dish.amount')}</span>
          <input
            id={amountId}
            inputMode="decimal"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            className="kw-field"
          />
        </label>
        {canPortion ? (
          <div role="group" aria-label={t('dish.unit')} className="flex gap-1">
            {(['g', 'portion'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={unit === option}
                onClick={() => setUnit(option)}
                className="kw-toggle"
              >
                {option === 'g' ? 'g' : food.portionName}
              </button>
            ))}
          </div>
        ) : (
          <span className="pb-3 text-sm text-ink-muted">g</span>
        )}
      </div>
      {unit === 'portion' && grams !== null ? (
        <p className="text-xs text-ink-muted">{t('dish.equalsGrams', { grams })}</p>
      ) : null}
      <button type="submit" disabled={grams === null || grams <= 0} className="kw-btn kw-btn-small">
        {submitLabel}
      </button>
    </form>
  );
}

function AddFreeLine({ recipeId }: { recipeId: string }): React.JSX.Element {
  const { t } = useTranslation();
  const lineId = useId();
  const [text, setText] = useState('');

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        void addFreeIngredient(recipeId, text, db).then(() => setText(''));
      }}
    >
      <label htmlFor={lineId} className="flex flex-col gap-1">
        <span className="kw-label">{t('dish.freeLine')}</span>
        <input
          id={lineId}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={t('dish.freeLineHint')}
          maxLength={200}
          className="kw-field"
        />
      </label>
      <p className="text-xs text-ink-muted">{t('dish.freeLineNote')}</p>
      <button type="submit" disabled={text.trim() === ''} className="kw-btn kw-btn-small">
        {t('nutrition.recipe.addIngredient')}
      </button>
    </form>
  );
}

// --- Schritte ----------------------------------------------------------------------

/**
 * Schritte als Liste: Gespeichert wird beim Verlassen eines Feldes und bei
 * jeder Änderung der Reihenfolge — nicht bei jedem Tastendruck, sonst stünde
 * für jeden Buchstaben eine Änderung in der Outbox.
 */
function StepsSection({ recipe }: { recipe: LocalRecipe }): React.JSX.Element {
  const { t } = useTranslation();
  const [steps, setSteps] = useState<string[]>(() => stepsOf(recipe));
  const focusLast = useRef(false);
  const list = useRef<HTMLOListElement>(null);

  useEffect(() => {
    if (!focusLast.current) return;
    focusLast.current = false;
    const fields = list.current?.querySelectorAll('textarea');
    fields?.[fields.length - 1]?.focus();
  }, [steps.length]);

  const save = (next: string[]) => {
    setSteps(next);
    void setRecipeSteps(recipe.id, next, db);
  };

  const move = (index: number, direction: -1 | 1) => {
    const next = [...steps];
    const other = index + direction;
    if (other < 0 || other >= next.length) return;
    [next[index], next[other]] = [next[other]!, next[index]!];
    save(next);
  };

  return (
    <section aria-labelledby="edit-steps" className="kw-tile flex flex-col gap-3">
      <h3 id="edit-steps" className="kw-kicker">
        {t('dish.steps')}
      </h3>
      {steps.length === 0 ? <p className="text-xs text-ink-muted">{t('dish.noSteps')}</p> : null}
      <ol ref={list} className="flex flex-col gap-3">
        {steps.map((step, index) => (
          <li key={index} className="flex gap-2">
            <span className="kw-figure w-6 shrink-0 pt-3 text-right text-accent">{index + 1}</span>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <textarea
                aria-label={t('dish.stepLabel', { number: index + 1 })}
                value={step}
                rows={2}
                maxLength={1000}
                onChange={(event) =>
                  setSteps(steps.map((entry, at) => (at === index ? event.target.value : entry)))
                }
                onBlur={() => void setRecipeSteps(recipe.id, steps, db)}
                className="kw-field"
              />
              <div className="flex gap-1 self-end">
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={t('dish.stepUp', { number: index + 1 })}
                  className="kw-btn kw-btn-quiet kw-btn-small w-11 px-0"
                >
                  ↑
                </button>
                <button
                  type="button"
                  disabled={index === steps.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={t('dish.stepDown', { number: index + 1 })}
                  className="kw-btn kw-btn-quiet kw-btn-small w-11 px-0"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => save(steps.filter((_, at) => at !== index))}
                  aria-label={t('dish.stepRemove', { number: index + 1 })}
                  className="kw-btn kw-btn-quiet kw-btn-small w-11 px-0 hover:border-danger hover:text-danger"
                >
                  ×
                </button>
              </div>
            </div>
          </li>
        ))}
      </ol>
      <button
        type="button"
        disabled={steps.length >= RECIPE_MAX_STEPS}
        onClick={() => {
          focusLast.current = true;
          // Leere Schritte bleiben nur auf dem Bildschirm; gespeichert wird ohne sie.
          setSteps([...normalizeSteps(steps), '']);
        }}
        className="kw-btn kw-btn-quiet kw-btn-small"
      >
        {t('dish.addStep')}
      </button>
    </section>
  );
}
